export type PageItem = number | "ellipsis";

const toPositiveInt = (value: number, fallback: number) => {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const clampPage = (page: number, totalPages: number): number => {
  const total = toPositiveInt(totalPages, 1);
  const current = Math.floor(Number(page));
  if (!Number.isFinite(current) || current < 1) return 1;
  return Math.min(current, total);
};

const range = (start: number, end: number): number[] => {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Compact page list that always includes the first and last page.
 * Example: 1 … 74 75 76 … 175
 */
export const getVisiblePageItems = (
  page: number,
  totalPages: number,
  siblingCount = 1
): PageItem[] => {
  const total = Math.max(0, Math.floor(Number(totalPages)) || 0);
  if (total <= 0) return [];

  const current = clampPage(page, total);
  const siblings = Math.max(0, Math.floor(Number(siblingCount)) || 0);
  const maxUntruncated = siblings * 2 + 5;

  if (total <= maxUntruncated) {
    return range(1, total);
  }

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + 2 * siblings;
    return [...range(1, Math.min(leftCount, total - 1)), "ellipsis", total];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = 3 + 2 * siblings;
    const start = Math.max(2, total - rightCount + 1);
    return [1, "ellipsis", ...range(start, total)];
  }

  return [
    1,
    "ellipsis",
    ...range(leftSibling, rightSibling),
    "ellipsis",
    total,
  ];
};

export const getPageRange = (
  page: number,
  pageSize: number,
  totalItems: number
): { from: number; to: number; total: number } => {
  const total = Math.max(0, Math.floor(Number(totalItems)) || 0);
  const size = toPositiveInt(pageSize, 1);
  if (total === 0) return { from: 0, to: 0, total: 0 };

  const totalPages = Math.max(1, Math.ceil(total / size));
  const current = clampPage(page, totalPages);
  const from = (current - 1) * size + 1;
  const to = Math.min(current * size, total);
  return { from, to, total };
};

export const resolveTotalPages = (
  totalPages: number,
  totalItems: number,
  pageSize: number
): number => {
  const fromServer = Math.floor(Number(totalPages));
  if (Number.isFinite(fromServer) && fromServer > 0) return fromServer;

  const items = Math.max(0, Math.floor(Number(totalItems)) || 0);
  const size = toPositiveInt(pageSize, 1);
  if (items <= 0) return 1;
  return Math.max(1, Math.ceil(items / size));
};
